import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Trash, PencilSimple, UserCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABELS = {
  super_admin: "Super Admin",
  head_office_admin: "Head Office Admin",
  branch_admin: "Branch Admin",
  operator: "Operator",
  accountant: "Accountant",
  customer_support: "Customer Support",
  document_executive: "Document Executive",
  print_operator: "Print Operator",
  doc_officer: "Doc Verification",
  complaint_manager: "Complaint Manager",
  service_manager: "Service Manager",
  inventory_manager: "Inventory Manager",
  reception: "Reception",
  viewer: "Viewer",
  view_only: "View Only",
};

const ROLE_TONE = {
  super_admin: "bg-red-100 text-red-800 border-red-200",
  head_office_admin: "bg-orange-100 text-orange-800 border-orange-200",
  branch_admin: "bg-blue-100 text-blue-800 border-blue-200",
  operator: "bg-emerald-100 text-emerald-800 border-emerald-200",
  accountant: "bg-purple-100 text-purple-800 border-purple-200",
  customer_support: "bg-cyan-100 text-cyan-800 border-cyan-200",
  document_executive: "bg-amber-100 text-amber-800 border-amber-200",
  print_operator: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-200",
  doc_officer: "bg-amber-100 text-amber-800 border-amber-200",
  complaint_manager: "bg-red-100 text-red-800 border-red-200",
  service_manager: "bg-indigo-100 text-indigo-800 border-indigo-200",
  inventory_manager: "bg-teal-100 text-teal-800 border-teal-200",
  reception: "bg-pink-100 text-pink-800 border-pink-200",
  viewer: "bg-slate-100 text-slate-700 border-slate-200",
  view_only: "bg-slate-100 text-slate-700 border-slate-200",
};

const PERMISSION_MODULES = [
  "Customers", "Applications", "Billing", "POS Billing",
  "Payments", "Collections", "Expenses", "Purchase",
  "Rent Agreement", "AI Draft", "Photo Studio", "Print Center",
  "Gov Portals", "WhatsApp Center", "Marketing", "Reports",
  "Document Manager",
];

const DEPARTMENTS = ["CSC Services", "Accounts", "Marketing", "Support", "Admin", "Print & Design", "Documents"];
const DESIGNATIONS = ["Operator", "Senior Operator", "Team Lead", "Branch Manager", "Accountant", "Support Executive", "Documents Officer", "Print Operator", "Head Office Manager"];

// Default permissions per role — Branch Admin & higher gets everything;
// Operators get core CSC modules; Accountants get money-related; etc.
const DEFAULT_PERMS = {
  super_admin: PERMISSION_MODULES,
  head_office_admin: PERMISSION_MODULES,
  branch_admin: PERMISSION_MODULES,
  operator: ["Customers", "Applications", "Billing", "POS Billing", "AI Draft", "Print Center", "Photo Studio", "Document Manager"],
  accountant: ["Payments", "Collections", "Expenses", "Purchase", "Reports", "Billing"],
  customer_support: ["Customers", "WhatsApp Center", "Applications"],
  document_executive: ["Customers", "Document Manager", "AI Draft", "Applications"],
  print_operator: ["Print Center", "Photo Studio", "Customers"],
  reception: ["Customers", "Applications"],
  viewer: ["Reports"],
  view_only: ["Reports"],
};

const EMPTY = {
  name: "", email: "", password: "", confirm_password: "", role: "operator",
  mobile: "", employee_id: "", department: "CSC Services", designation: "Operator",
  reporting_to: "", address: "", remarks: "", id_proof: "",
  branch: "Main", office_type: "branch",
  joining_date: "", aadhaar: "", salary: 0, status: "active",
  permissions: DEFAULT_PERMS.operator,
};

export default function Staff() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      setRows((await api.get("/staff")).data);
      const s = await api.get("/staff/stats");
      setStats(s.data);
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) { toast.error("Name, email, and password are required"); return; }
    if (form.password !== form.confirm_password) { toast.error("Passwords don't match"); return; }
    try {
      const { confirm_password, ...body } = form;
      await api.post("/staff", body);
      toast.success("Staff created");
      setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      const payload = { ...edit };
      if (!payload.password) delete payload.password;
      await api.patch(`/staff/${edit.id}`, payload);
      toast.success("Staff updated");
      setEdit(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try { await api.delete(`/staff/${row.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-[1400px]">
      <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-orange-600 font-semibold mb-2">Team</div>
          <h1 className="text-4xl font-bold text-slate-900" style={{ fontFamily: "var(--font-heading)" }}>Staff & Roles</h1>
          <p className="text-sm text-slate-600 mt-2">Head Office + branch-wise staff management with role-scoped edit rights.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-staff-btn" className="bg-orange-600 hover:bg-orange-700 text-white h-11 gap-2 hover:-translate-y-px hover:shadow-md transition-[transform,box-shadow,background-color] duration-200">
              <Plus size={18} weight="bold" /> New Staff
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg bg-white">
            <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Add Staff Member</DialogTitle></DialogHeader>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <F label="Full Name *"><Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></F>
              <F label="Employee ID"><Input value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })} /></F>
              <F label="Email *"><Input data-testid="staff-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></F>
              <F label="Password *"><Input data-testid="staff-password-input" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></F>
              <F label="Confirm Password *"><Input data-testid="staff-confirm-password" type="password" value={form.confirm_password} onChange={(e) => setForm({ ...form, confirm_password: e.target.value })} /></F>
              <F label="Role">
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v, permissions: DEFAULT_PERMS[v] || form.permissions })}>
                  <SelectTrigger data-testid="staff-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Office Type *">
                <div className="flex gap-2" data-testid="staff-office-type">
                  {[
                    { key: "branch",      label: "🏬 Branch Office" },
                    { key: "head_office", label: "🏢 Head Office" },
                  ].map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      onClick={() => setForm({ ...form, office_type: o.key, ...(o.key === "head_office" ? { branch: "Head Office" } : {}) })}
                      data-testid={`office-type-${o.key}`}
                      className={`flex-1 h-10 rounded-md border text-xs font-semibold transition-colors ${form.office_type === o.key ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200 hover:border-orange-400"}`}
                    >
                      {o.label}
                    </button>
                  ))}
                </div>
              </F>
              <F label="Mobile"><Input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} /></F>
              <F label="Department">
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="staff-department"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{DEPARTMENTS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Designation">
                <Select value={form.designation} onValueChange={(v) => setForm({ ...form, designation: v })}>
                  <SelectTrigger data-testid="staff-designation"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">{DESIGNATIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Reporting To"><Input value={form.reporting_to} onChange={(e) => setForm({ ...form, reporting_to: e.target.value })} placeholder="Branch Manager / HO Head" /></F>
              <F label="Branch">
                <Input
                  value={form.branch}
                  disabled={form.office_type === "head_office"}
                  onChange={(e) => setForm({ ...form, branch: e.target.value })}
                  data-testid="staff-branch-input"
                />
              </F>
              <F label="Salary (₹/month)"><Input type="number" min={0} value={form.salary} onChange={(e) => setForm({ ...form, salary: Number(e.target.value) })} /></F>
              <F label="Joining Date"><Input type="date" value={form.joining_date} onChange={(e) => setForm({ ...form, joining_date: e.target.value })} /></F>
              <F label="Aadhaar"><Input value={form.aadhaar} onChange={(e) => setForm({ ...form, aadhaar: e.target.value })} /></F>
              <F label="ID Proof (Aadhaar/PAN URL)"><Input value={form.id_proof} onChange={(e) => setForm({ ...form, id_proof: e.target.value })} placeholder="https://…" /></F>
              <F label="Address"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></F>
              <F label="Remarks"><Input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></F>
            </div>

            {/* Permissions grid */}
            <div className="mt-6 pt-4 border-t border-slate-100" data-testid="staff-permissions">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">Module Access · Permissions</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">Defaults are auto-set from the role — tick / untick to override.</div>
                </div>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, permissions: PERMISSION_MODULES })}
                    className="px-2.5 h-7 rounded-md text-[11px] font-semibold bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100"
                    data-testid="perms-all"
                  >All</button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, permissions: [] })}
                    className="px-2.5 h-7 rounded-md text-[11px] font-semibold bg-red-50 text-red-800 border border-red-200 hover:bg-red-100"
                    data-testid="perms-none"
                  >None</button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, permissions: DEFAULT_PERMS[form.role] || [] })}
                    className="px-2.5 h-7 rounded-md text-[11px] font-semibold bg-orange-50 text-orange-800 border border-orange-200 hover:bg-orange-100"
                    data-testid="perms-default"
                  >Role Default</button>
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                {PERMISSION_MODULES.map((m) => {
                  const on = (form.permissions || []).includes(m);
                  return (
                    <button
                      key={m}
                      type="button"
                      onClick={() => {
                        const list = new Set(form.permissions || []);
                        on ? list.delete(m) : list.add(m);
                        setForm({ ...form, permissions: [...list] });
                      }}
                      data-testid={`perm-${m.toLowerCase().replace(/\s+/g, "-")}`}
                      className={`px-2.5 h-9 rounded-md border text-xs font-medium text-left transition-colors ${on ? "bg-orange-500 text-white border-orange-500" : "bg-white text-slate-700 border-slate-200 hover:border-orange-400"}`}
                    >
                      {on ? "✓ " : ""}{m}
                    </button>
                  );
                })}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => { setForm(EMPTY); }}>Reset</Button>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="staff-save-btn" onClick={create} className="bg-orange-600 hover:bg-orange-700 text-white">Save Staff</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {stats && (
        <div className="mb-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3" data-testid="staff-stats">
          <div className="p-4 rounded-xl bg-slate-900 text-white">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">Total Staff</div>
            <div className="text-2xl font-bold mt-1">{stats.total}</div>
            <div className="text-[10px] text-emerald-300 mt-0.5">{stats.active} active · {stats.inactive} inactive</div>
          </div>
          <div className="p-4 rounded-xl bg-orange-50 border border-orange-200" data-testid="staff-stats-hq">
            <div className="text-[10px] uppercase tracking-widest text-orange-700 font-semibold">🏢 Head Office</div>
            <div className="text-2xl font-bold mt-1 text-orange-900">{stats.head_office}</div>
            <div className="text-[10px] text-orange-700 mt-0.5">Cross-branch access</div>
          </div>
          {stats.per_branch.slice(0, 4).map((b) => (
            <div key={b.branch} className="p-4 rounded-xl bg-white border border-slate-200" data-testid={`staff-stats-branch-${b.branch}`}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">🏬 {b.branch}</div>
              <div className="text-2xl font-bold mt-1 text-slate-900">{b.count}</div>
              <div className="text-[10px] text-slate-500 mt-0.5">Branch staff</div>
            </div>
          ))}
        </div>
      )}

      <Card className="bg-white border-slate-200 overflow-hidden" data-testid="staff-table">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Employee</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Role</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Office</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Department</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Branch</TableHead>
              <TableHead className="text-xs uppercase tracking-[0.15em] text-slate-500">Status</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (<TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-16">No staff yet.</TableCell></TableRow>)}
            {rows.map((r) => (
              <TableRow key={r.id} className="hover:bg-slate-50">
                <TableCell>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><UserCircle size={22} className="text-slate-500" /></div>
                    <div>
                      <div className="font-medium text-slate-900">{r.name}</div>
                      <div className="text-xs text-slate-500">{r.email}{r.employee_id ? ` · ${r.employee_id}` : ""}</div>
                    </div>
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline" className={`${ROLE_TONE[r.role] || ""} font-medium text-[10px] uppercase tracking-wider`}>{ROLE_LABELS[r.role] || r.role}</Badge></TableCell>
                <TableCell className="text-sm">{r.department || "—"}</TableCell>
                <TableCell className="text-sm">{r.branch || "—"}</TableCell>
                <TableCell>{r.status === "active" ? <Badge className="bg-emerald-600 hover:bg-emerald-600 text-white">Active</Badge> : <Badge variant="outline" className="border-slate-300">Inactive</Badge>}</TableCell>
                <TableCell>
                  <div className="flex gap-1 justify-end">
                    <Button variant="ghost" size="icon" onClick={() => setEdit({ ...r, password: "" })} data-testid={`staff-edit-${r.id}`}><PencilSimple size={16} /></Button>
                    {r.id !== user?.id && (
                      <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => del(r)} data-testid={`staff-delete-${r.id}`}><Trash size={16} /></Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader><DialogTitle style={{ fontFamily: "var(--font-heading)" }}>Edit Staff</DialogTitle></DialogHeader>
          {edit && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-2">
              <F label="Name"><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} /></F>
              <F label="Employee ID"><Input value={edit.employee_id || ""} onChange={(e) => setEdit({ ...edit, employee_id: e.target.value })} /></F>
              <F label="Role">
                <Select value={edit.role} onValueChange={(v) => setEdit({ ...edit, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(ROLE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                </Select>
              </F>
              <F label="Status">
                <Select value={edit.status} onValueChange={(v) => setEdit({ ...edit, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
                </Select>
              </F>
              <F label="Mobile"><Input value={edit.mobile || ""} onChange={(e) => setEdit({ ...edit, mobile: e.target.value })} /></F>
              <F label="Department"><Input value={edit.department || ""} onChange={(e) => setEdit({ ...edit, department: e.target.value })} /></F>
              <F label="Branch"><Input value={edit.branch || ""} onChange={(e) => setEdit({ ...edit, branch: e.target.value })} /></F>
              <F label="New Password (leave blank to keep)"><Input type="password" value={edit.password || ""} onChange={(e) => setEdit({ ...edit, password: e.target.value })} /></F>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEdit(null)}>Cancel</Button>
            <Button onClick={save} className="bg-orange-600 hover:bg-orange-700 text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-600">{label}</Label>
      {children}
    </div>
  );
}
